import { useCallback, useEffect, useRef } from "react";
import { useSnapshot } from "valtio";
import { MediaService } from "@services/media/MediaService";
import { resolvedAdStore } from "@/store/resolvedAdStore";
import type { ResolvedAdContent } from "@services/media/commands/cmdResolveAds";

const mediaService = new MediaService();
const pendingIds = new Set<number>();
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

/*
    How many times a batch may fail before the cards in it stop trying.

    A card with no resolved content renders the loading skeleton, and the skeleton has no end
    state: it is indistinguishable from "still loading" forever. A single dropped request
    therefore used to brick a card until the page was reloaded, which is what a direct mail
    card on the Ads page was doing (MPN-1948).

    Bounded rather than infinite: an ad that genuinely cannot resolve should stop asking rather
    than retry on every scroll for as long as the tab is open.
*/
const MAX_ATTEMPTS = 3;

const attempts = new Map<number, number>();

async function flushBatch() {
    const ids = [...pendingIds].filter((id) => !(id in resolvedAdStore.byId));
    pendingIds.clear();
    if (ids.length === 0) return;

    try {
        const result = await mediaService.resolveAdDisplay(ids);
        resolvedAdStore.byId = { ...resolvedAdStore.byId, ...result.resolved };
        ids.forEach((id) => attempts.delete(id));
    } catch (error) {
        // Not silent. A resolve that fails leaves cards looking like they are still loading,
        // so the one place that knows why has to say so.
        const next = ids.filter((id) => {
            const tried = (attempts.get(id) ?? 0) + 1;
            attempts.set(id, tried);
            return tried < MAX_ATTEMPTS;
        });
        console.warn(
            `Could not resolve ${ids.length} ad(s); retrying ${next.length}`, error,
        );

        if (next.length) {
            // Backed off, so a server having a bad minute is not hammered by every card on
            // screen at once.
            const delay = 400 * Math.max(...next.map((id) => attempts.get(id) ?? 1));
            next.forEach((id) => pendingIds.add(id));
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = setTimeout(flushBatch, delay);
            return;
        }

        // Out of attempts. Recorded as resolved-to-nothing so the card shows its empty state
        // rather than a skeleton that never resolves: "there is nothing here" is wrong, but
        // it is legible and it stops, which an endless shimmer does not.
        const gaveUp: Record<number, null> = {};
        ids.forEach((id) => { gaveUp[id] = null; });
        resolvedAdStore.byId = { ...resolvedAdStore.byId, ...gaveUp };
    }
}

export function requestResolveAdContent(adId: number) {
    // `in`, not truthiness: an ad that resolved to nothing is stored as null and
    // must not be re-requested on every render.
    if (adId in resolvedAdStore.byId) return;
    pendingIds.add(adId);
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(flushBatch, 150);
}

export function useResolveAdContent(
    adId: number,
    skip = false,
): [React.RefCallback<Element>, ResolvedAdContent | null | undefined] {
    const { byId } = useSnapshot(resolvedAdStore);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const resolvedRef = useRef(false);

    useEffect(() => {
        return () => observerRef.current?.disconnect();
    }, []);

    const ref = useCallback((node: Element | null) => {
        observerRef.current?.disconnect();
        if (skip || !node || resolvedRef.current) return;

        observerRef.current = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    resolvedRef.current = true;
                    observerRef.current?.disconnect();
                    requestResolveAdContent(adId);
                }
            },
            { rootMargin: "200px" },
        );
        observerRef.current.observe(node);
    }, [adId, skip]);

    return [ref, skip ? undefined : (byId[adId] as ResolvedAdContent | null | undefined)];
}
